import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useStartRefinementMutation } from '../store/apis/rubric-refinement.api'
import { rubricRefinementSelectors } from '../store/selectors/rubricRefinement.selectors'
import { useFetchQuestionsQuery } from '../store/apis/question.api'
import { useVersionId } from '../utils/parseURL.helper'
import { useParams } from 'react-router'
import { flatten } from '../utils/array.helper'

/**
 * Custom hook to handle the rubric refinement initialization process.
 *
 * This hook:
 * 1. Starts the refinement process in the background when the component mounts
 * 2. Monitors the question's processing status
 * 3. Restarts the refinement process when initialization is complete
 * 4. Only shows loading indicators after the user has clicked "Let's get started"
 *
 * @returns {Object} An object containing the loading state
 */
export const useRefinementInitialization = () => {
    // Redux state
    const refinementProcess = useSelector(rubricRefinementSelectors.refinementProcess)
    const refinementItems = useSelector(rubricRefinementSelectors.refinementItems) || []
    const hasStarted = useSelector(rubricRefinementSelectors.hasStarted)

    // Route params
    const { token, layerId: questionId } = useParams()
    const versionId = useVersionId()

    // API hooks
    const [startRefinement, { isLoading: isStartLoading }] = useStartRefinementMutation()
    const { data: questions, isFetching: isQuestionsFetching } = useFetchQuestionsQuery({ token, versionId })

    // Refs to track state between renders
    const prevStatusRef = useRef(null)
    const hasInitializedRef = useRef(false)

    // Start the refinement process when the component mounts
    useEffect(() => {
        if (!hasInitializedRef.current) {
            startRefinement({ questionId, token, versionId })
            hasInitializedRef.current = true
        }
    }, [startRefinement, questionId, token, versionId])

    // Monitor the question's processing status and restart refinement when initialization is complete
    useEffect(() => {
        const currentQuestion = flatten(questions)?.find(q => q.id === Number(questionId))
        const currentStatus = currentQuestion?.processing?.status

        const isInitializationComplete =
            prevStatusRef.current === 'ip' && // Previous status was REFINEMENT_INITIALIZING
            currentStatus === 'f' && // Current status is READY
            refinementProcess?.status === 'Initializing' // But refinement process still shows initializing

        if (isInitializationComplete) {
            startRefinement({ questionId, token, versionId })
        }

        // Update the previous status for the next check
        if (currentStatus) {
            prevStatusRef.current = currentStatus
        }
    }, [questions, questionId, startRefinement, token, versionId, refinementProcess])

    // Only consider the process to be loading if the user has started the refinement
    const isLoading =
        hasStarted &&
        (isStartLoading ||
            isQuestionsFetching ||
            !refinementItems.length ||
            refinementProcess?.status === 'Initializing')

    return { isLoading }
}
